// «Números y conexión» de la app WhatsApp: los números de la org (Meta Cloud
// API y Twilio, con su estado en vivo) y «Conectar WhatsApp» (Embedded Signup
// con coexistencia: el número sigue en la app del teléfono). Antes la conexión
// estaba en Empresa → Configuración → WhatsApp; ahí solo queda para orgs que
// aún no tienen número (sin número la app no se enciende).
//
// Solo lectura de lo que ya da la API: /admin/whatsapp/app/overview +
// /numbers/{key}/status (Orión). Dar de baja o cambiar el número principal no
// existe todavía en la API → no se ofrece.

import { BadgeCheck, Info, Phone } from 'lucide-react';

import { WhatsAppCoexistenceConnect } from '@/components/meta/WhatsAppCoexistenceConnect';
import { numberLabel, type WaNumber, type WaNumberStatus, type WaOverview } from '../api';
import { prettyPhone } from './shared';
import { Card, H, ScrollPane } from './Sections';

type Notify = (type: 'success' | 'error' | 'info', title: string, message?: string) => void;

const QUALITY: Record<string, { label: string; cls: string }> = {
  GREEN: { label: 'Calidad alta', cls: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300' },
  YELLOW: { label: 'Calidad media', cls: 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300' },
  RED: { label: 'Calidad baja', cls: 'border-rose-500/40 bg-rose-500/10 text-rose-800 dark:text-rose-300' },
};

export function NumbersView({ overview, statuses, notify, onConnected }: {
  overview: WaOverview | null;
  statuses: Record<string, WaNumberStatus | undefined>;
  notify: Notify;
  onConnected: () => void;
}) {
  const nums = overview?.numbers || [];
  const hasTwilio = nums.some((n) => n.provider === 'twilio');
  return (
    <ScrollPane>
      <H title="Números conectados" sub="Los números de WhatsApp de tu organización y cómo los ve WhatsApp ahora mismo." />
      {!overview && <div className="h-28 animate-pulse rounded-2xl bg-muted" aria-hidden />}
      {overview && nums.length === 0 && (
        <Card><p className="text-[14px]">Tu organización todavía no tiene un número de WhatsApp conectado.</p></Card>
      )}
      <div className="space-y-3">
        {nums.map((n) => <NumberCard key={n.key} n={n} live={statuses[n.key]} />)}
      </div>
      {hasTwilio && (
        <p className="mt-3 flex items-start gap-2 text-[12.5px] text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          Los números de Twilio los da de alta el equipo de Orion; si necesitas cambiar uno, pídelo a soporte.
        </p>
      )}

      <div className="mt-8">
        <H title="Conectar otro número" />
        <WhatsAppCoexistenceConnect
          className=""
          showNumbers={false}
          onConnected={onConnected}
          notify={(type, msg) => notify(type, msg)}
        />
      </div>
    </ScrollPane>
  );
}

function NumberCard({ n, live }: { n: WaNumber; live?: WaNumberStatus }) {
  const q = live?.quality_rating ? QUALITY[String(live.quality_rating).toUpperCase()] : null;
  const connected = n.enabled && (n.provider !== 'meta' || (live ? live.live && !live.error : true));
  return (
    <Card>
      <div className="flex flex-wrap items-start gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-muted"><Phone className="h-5 w-5" aria-hidden /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[16px] font-semibold tabular-nums">{prettyPhone(numberLabel(n, live))}</span>
            {n.is_default && <span className="rounded-md border border-border px-1.5 py-0.5 text-[11px] font-medium">Principal</span>}
            <span className="rounded-md border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground">{n.provider === 'meta' ? 'WhatsApp Cloud API' : 'Twilio'}</span>
          </div>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            {live?.verified_name ? <span className="inline-flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5" aria-hidden />{live.verified_name}</span> : (n.display_name || '—')}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5 text-[12px]">
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium ${connected
              ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'
              : 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300'}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${connected ? 'bg-emerald-600' : 'bg-amber-500'}`} aria-hidden />
              {!n.enabled ? 'Deshabilitado' : connected ? 'Conectado' : 'Revisar conexión'}
            </span>
            {q && <span className={`rounded-full border px-2 py-0.5 font-medium ${q.cls}`}>{q.label}</span>}
            {live?.messaging_limit_tier && <span className="rounded-full border border-border px-2 py-0.5 text-muted-foreground">Límite: {String(live.messaging_limit_tier).replace('TIER_', '').replace('_', ' ')} conversaciones/día</span>}
            {live?.name_status && live.name_status !== 'APPROVED' && <span className="rounded-full border border-border px-2 py-0.5 text-muted-foreground">Nombre: {live.name_status}</span>}
          </div>
          {live?.error && <p className="mt-2 text-[12.5px] text-amber-800 dark:text-amber-300">Meta no respondió el estado: {live.error}</p>}
          {live?.nota && <p className="mt-2 text-[12.5px] text-muted-foreground">{live.nota}</p>}
        </div>
      </div>
    </Card>
  );
}

